import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { 
  BookOpen, ArrowLeft, FileText, Loader2, Check, 
  Lightbulb, Star, Quote, Info, ChevronDown, Sparkles, AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Structure des sections du commentaire avec conseils
const COMMENTARY_SECTIONS = {
  introduction: {
    title: "Introduction",
    placeholder: "Redigez votre introduction ici...",
    tips: [
      "Presentez brievement l'auteur et l'oeuvre d'ou est tire le texte",
      "Situez le passage dans l'oeuvre si possible",
      "Enoncez clairement le THEME du texte (de quoi parle-t-il ?)",
      "Formulez la THESE de l'auteur (quelle position defend-il ?)",
      "Annoncez le plan de votre explication (les grandes parties du texte)"
    ],
    minWords: 80
  },
  problematique: {
    title: "Problematique",
    placeholder: "Quelle question philosophique ce texte pose-t-il ?",
    tips: [
      "Transformez la these de l'auteur en question",
      "Montrez pourquoi cette question est philosophiquement interessante",
      "La problematique doit creer une tension, un enjeu",
      "Exemple: Si la these est 'La conscience fait de l'homme une exception', la problematique pourrait etre 'En quoi la conscience distingue-t-elle l'homme des autres etres ?'"
    ],
    minWords: 40
  },
  plan: {
    title: "Annonce du plan",
    placeholder: "Annoncez les grandes parties de votre explication...",
    tips: [
      "Découpez le texte en 2 ou 3 mouvements logiques",
      "Chaque partie correspond à une étape de l'argumentation",
      "Indiquez les lignes correspondant à chaque partie",
      "Exemple: 'Nous verrons d'abord que... (l.1-5), puis que... (l.6-10), enfin que... (l.11-15)'"
    ],
    minWords: 30
  },
  partie1: {
    title: "Première partie - Explication",
    placeholder: "Expliquez la premiere partie du texte...",
    tips: [
      "Citez le texte entre guillemets pour appuyer votre analyse",
      "Expliquez les concepts cles utilises par l'auteur",
      "Montrez la logique de l'argumentation",
      "Faites des references a d'autres philosophes pour eclairer le propos",
      "N'oubliez pas: EXPLIQUER = dire ce que l'auteur veut dire + pourquoi il le dit"
    ],
    minWords: 100
  },
  partie2: {
    title: "Deuxieme partie - Explication",
    placeholder: "Expliquez la deuxieme partie du texte...",
    tips: [
      "Montrez la progression par rapport a la premiere partie",
      "Continuez a citer precisement le texte",
      "Analysez les connecteurs logiques (donc, car, mais, or...)",
      "Reliez les concepts entre eux"
    ],
    minWords: 100
  },
  partie3: {
    title: "Troisieme partie - Explication (si applicable)",
    placeholder: "Expliquez la troisieme partie du texte si necessaire...",
    tips: [
      "Cette partie n'est pas toujours necessaire",
      "Elle correspond souvent a la conclusion de l'auteur",
      "Montrez comment l'auteur aboutit a sa these"
    ],
    minWords: 0
  },
  discussion: {
    title: "Discussion critique",
    placeholder: "Discutez la these de l'auteur...",
    tips: [
      "Evaluez la force des arguments de l'auteur",
      "Proposez des objections possibles (d'autres philosophes)",
      "Montrez les limites eventuelles de la these",
      "Attention: critiquer ne veut pas dire rejeter, mais examiner avec rigueur",
      "Vous pouvez aussi montrer l'actualite ou la pertinence de la these"
    ],
    minWords: 80
  },
  conclusion: {
    title: "Conclusion",
    placeholder: "Concluez votre commentaire...",
    tips: [
      "Resumez brievement le parcours de votre explication",
      "Rappelez la these de l'auteur et son interet",
      "Ouvrez sur une question plus large soulevee par le texte",
      "Ne pas introduire de nouvelles idees"
    ],
    minWords: 50
  }
};

const CommentaireModule = ({ user }) => {
  const navigate = useNavigate();
  const [textes, setTextes] = useState([]);
  const [selectedPhilosopher, setSelectedPhilosopher] = useState("");
  const [currentExercise, setCurrentExercise] = useState(null);
  const [sections, setSections] = useState({
    introduction: "",
    problematique: "",
    plan: "",
    partie1: "",
    partie2: "",
    partie3: "",
    discussion: "",
    conclusion: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [aiHints, setAiHints] = useState(null);
  const [loadingHints, setLoadingHints] = useState(false);

  useEffect(() => {
    fetchTextes();
  }, []);

  const fetchTextes = async () => {
    try {
      const response = await axios.get(`${API}/program/textes`);
      setTextes(response.data);
    } catch (error) {
      console.error("Error fetching textes:", error);
    }
  };

  const generateExercise = async () => {
    setLoading(true);
    setResult(null);
    setSections({
      introduction: "",
      problematique: "",
      plan: "",
      partie1: "",
      partie2: "",
      partie3: "",
      discussion: "",
      conclusion: ""
    });
    setAiHints(null);
    
    try {
      const response = await axios.post(`${API}/exercises/generate`, {
        student_id: user.id,
        exercise_type: "commentaire",
        philosopher: selectedPhilosopher && selectedPhilosopher !== "all" ? selectedPhilosopher : undefined
      });
      setCurrentExercise(response.data);
      toast.success("Texte genere !");
      
      // Auto-fetch AI hints
      fetchAiHints(response.data);
    } catch (error) {
      console.error("Error generating exercise:", error);
      toast.error("Erreur lors de la generation");
    } finally {
      setLoading(false);
    }
  };

  const fetchAiHints = async (exercise) => {
    setLoadingHints(true);
    try {
      const response = await axios.post(`${API}/exercises/hints`, {
        exercise_id: exercise.id,
        exercise_type: "commentaire",
        topic: exercise.topic,
        content: exercise.content,
        notion: exercise.notion,
        student_id: user.id
      });
      setAiHints(response.data);
    } catch (error) {
      console.error("Error fetching hints:", error);
    } finally {
      setLoadingHints(false);
    }
  };

  const updateSection = (key, value) => {
    setSections(prev => ({ ...prev, [key]: value }));
  };

  const getTotalWords = () => {
    return Object.values(sections).join(" ").trim().split(/\s+/).filter(w => w).length;
  };

  const getSectionWordCount = (key) => {
    return sections[key].trim().split(/\s+/).filter(w => w).length;
  };

  const submitAnswer = async () => {
    const totalWords = getTotalWords();
    if (totalWords < 300) {
      toast.error("Votre commentaire doit contenir au moins 300 mots au total");
      return;
    }

    // Check required sections
    const requiredSections = ["introduction", "problematique", "partie1", "conclusion"];
    for (const key of requiredSections) {
      const config = COMMENTARY_SECTIONS[key];
      if (getSectionWordCount(key) < config.minWords) {
        toast.error(`La section "${config.title}" doit contenir au moins ${config.minWords} mots`);
        return;
      }
    }

    setSubmitting(true);
    
    // Combine all sections into formatted answer
    const formattedAnswer = Object.entries(sections)
      .filter(([key, value]) => value.trim())
      .map(([key, value]) => `## ${COMMENTARY_SECTIONS[key].title}\n\n${value}`)
      .join("\n\n---\n\n");
    
    try {
      const response = await axios.post(`${API}/exercises/submit`, {
        exercise_id: currentExercise.id,
        student_id: user.id,
        answer: formattedAnswer,
        sections: sections
      });
      setResult(response.data);
      toast.success("Commentaire evalue !");
    } catch (error) {
      console.error("Error submitting:", error);
      toast.error("Erreur lors de l'evaluation");
    } finally {
      setSubmitting(false);
    }
  };

  const uniquePhilosophers = [...new Set(textes.map(t => t.philosopher))];

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="commentaire-page">
      {/* Navigation */}
      <nav className="navbar px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/dashboard")}
            className="text-white hover:bg-white/10"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <span className="navbar-logo hidden sm:block">Commentaire de texte</span>
        </div>
      </nav>

      <main className="px-4 py-6 max-w-5xl mx-auto page-enter">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl text-[#1A365D] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Module Commentaire de Texte
          </h1>
          <p className="text-[#64748B]">
            Analysez les grands textes philosophiques avec une methode structuree
          </p>
        </div>

        {!currentExercise ? (
          <Card className="card-sjh" data-testid="text-selector">
            <CardHeader>
              <CardTitle className="text-lg text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Choisir un texte
              </CardTitle>
              <CardDescription>
                Selectionnez un philosophe ou laissez le hasard decider
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-[#0F172A] mb-2 block">
                  Philosophe (optionnel)
                </label>
                <Select value={selectedPhilosopher} onValueChange={setSelectedPhilosopher}>
                  <SelectTrigger className="border-[#E2E8F0]" data-testid="philosopher-select">
                    <SelectValue placeholder="Tous les philosophes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les philosophes</SelectItem>
                    {uniquePhilosophers.map((philosopher, idx) => (
                      <SelectItem key={idx} value={philosopher}>{philosopher}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button 
                onClick={generateExercise}
                disabled={loading}
                className="w-full bg-[#1A365D] hover:bg-[#132745]"
                data-testid="generate-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generation...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    Générer un texte
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        ) : result ? (
          <Card className="card-sjh" data-testid="result-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Evaluation de votre commentaire
                </CardTitle>
                <div className="flex items-center gap-2 bg-[#D4AF37]/10 px-4 py-2 rounded-lg">
                  <Star className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-xl font-bold text-[#1A365D]">{result.score}/20</span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Section-by-section feedback */}
              {result.section_feedback && (
                <Accordion type="multiple" className="space-y-2">
                  {Object.entries(result.section_feedback).map(([key, feedback]) => (
                    <AccordionItem key={key} value={key} className="border rounded-lg px-4">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{COMMENTARY_SECTIONS[key]?.title || key}</span>
                          <Badge className={feedback.score >= 15 ? "bg-green-500" : feedback.score >= 10 ? "bg-yellow-500" : "bg-red-500"}>
                            {feedback.score}/20
                          </Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="text-sm text-[#64748B]">
                        {feedback.comment}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}

              {/* Strengths */}
              <div>
                <h3 className="flex items-center gap-2 font-semibold text-[#22C55E] mb-3">
                  <Check className="w-5 h-5" />
                  Points forts
                </h3>
                <ul className="space-y-2">
                  {result.strengths?.map((s, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-[#0F172A]">
                      <span className="text-[#22C55E]">•</span>
                      {s}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Improvements */}
              <div>
                <h3 className="flex items-center gap-2 font-semibold text-[#EAB308] mb-3">
                  <Lightbulb className="w-5 h-5" />
                  Axes d'amelioration
                </h3>
                <ul className="space-y-2">
                  {result.improvements?.map((i, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-[#0F172A]">
                      <span className="text-[#EAB308]">•</span>
                      {i}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Feedback */}
              <div className="bg-[#F8F9FA] rounded-lg p-4">
                <h3 className="font-semibold text-[#1A365D] mb-2">Commentaire du professeur</h3>
                <p className="text-sm text-[#64748B] whitespace-pre-wrap">{result.feedback}</p>
              </div>

              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  onClick={() => {
                    setCurrentExercise(null);
                    setResult(null);
                    setSections({
                      introduction: "",
                      problematique: "",
                      plan: "",
                      partie1: "",
                      partie2: "",
                      partie3: "",
                      discussion: "",
                      conclusion: ""
                    });
                    setAiHints(null);
                  }}
                  className="flex-1 border-[#1A365D] text-[#1A365D]"
                  data-testid="new-exercise-button"
                >
                  Nouveau texte
                </Button>
                <Button 
                  onClick={() => navigate("/dashboard")}
                  className="flex-1 bg-[#1A365D] hover:bg-[#132745]"
                >
                  Retour au tableau de bord
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Exercise Card - Full Text */}
            <Card className="card-sjh" data-testid="exercise-card">
              <CardHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-[#1A365D]">Commentaire</Badge>
                  <Badge className="bg-[#D4AF37] text-[#0F172A]">{currentExercise.notion}</Badge>
                  {currentExercise.philosopher && (
                    <Badge variant="outline">{currentExercise.philosopher}</Badge>
                  )}
                </div>
                <CardTitle className="text-xl text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {currentExercise.topic}
                </CardTitle>
                {currentExercise.source && (
                  <CardDescription className="text-sm">
                    Source : {currentExercise.source}
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <div className="bg-[#1A365D] text-white rounded-lg p-6">
                  <Quote className="w-8 h-8 text-[#D4AF37] mb-4" />
                  <div className="text-sm whitespace-pre-wrap font-serif leading-relaxed max-h-[400px] overflow-y-auto">
                    {currentExercise.content}
                  </div>
                  {currentExercise.reference && (
                    <p className="mt-4 text-xs text-white/70 italic">
                      — {currentExercise.reference}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* AI Hints Card */}
            {(loadingHints || aiHints) && (
              <Card className="card-sjh border-[#D4AF37]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-[#1A365D] flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                    Pistes de reflexion (generees par SophIA)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingHints ? (
                    <div className="flex items-center gap-2 text-[#64748B]">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generation des pistes...
                    </div>
                  ) : aiHints && (
                    <div className="space-y-4">
                      {aiHints.key_concepts && (
                        <div>
                          <h4 className="font-semibold text-[#1A365D] text-sm mb-2">Concepts cles a analyser</h4>
                          <div className="flex flex-wrap gap-2">
                            {aiHints.key_concepts.map((c, i) => (
                              <Badge key={i} variant="outline" className="bg-[#F0F9FF]">{c}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {aiHints.suggested_authors && (
                        <div>
                          <h4 className="font-semibold text-[#1A365D] text-sm mb-2">Philosophes a mobiliser</h4>
                          <ul className="text-sm text-[#64748B] space-y-1">
                            {aiHints.suggested_authors.map((a, i) => (
                              <li key={i}>• <strong>{a.name}</strong>: {a.relevance}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {aiHints.thesis_direction && (
                        <div>
                          <h4 className="font-semibold text-[#1A365D] text-sm mb-2">Direction de la these</h4>
                          <p className="text-sm text-[#64748B]">{aiHints.thesis_direction}</p>
                        </div>
                      )}
                      {aiHints.possible_objections && (
                        <div>
                          <h4 className="font-semibold text-[#1A365D] text-sm mb-2">Objections possibles</h4>
                          <ul className="text-sm text-[#64748B] space-y-1">
                            {aiHints.possible_objections.map((o, i) => (
                              <li key={i}>• {o}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Section-by-Section Input */}
            <Card className="card-sjh">
              <CardHeader>
                <CardTitle className="text-lg text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Rédigez votre commentaire
                </CardTitle>
                <CardDescription>
                  Complétez chaque section. Les conseils vous guident.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion type="multiple" defaultValue={["introduction"]} className="space-y-3">
                  {Object.entries(COMMENTARY_SECTIONS).map(([key, config]) => (
                    <AccordionItem 
                      key={key} 
                      value={key} 
                      className="border rounded-lg overflow-hidden"
                    >
                      <AccordionTrigger className="px-4 hover:no-underline hover:bg-[#F8F9FA]">
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${
                            getSectionWordCount(key) >= config.minWords ? 'bg-green-500' : 
                            sections[key].trim() ? 'bg-yellow-500' : 'bg-gray-300'
                          }`} />
                          <span className="font-medium text-[#1A365D]">{config.title}</span>
                          <span className="text-xs text-[#64748B]">
                            ({getSectionWordCount(key)} mots{config.minWords > 0 ? ` / min ${config.minWords}` : ''})
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4">
                        {/* Tips Box */}
                        <div className="mb-3 p-3 bg-[#FEF3C7] rounded-lg border border-[#FCD34D]">
                          <div className="flex items-start gap-2">
                            <Info className="w-4 h-4 text-[#92400E] mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-xs font-semibold text-[#92400E] mb-1">Conseils :</p>
                              <ul className="text-xs text-[#92400E] space-y-1">
                                {config.tips.map((tip, i) => (
                                  <li key={i}>• {tip}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                        
                        <Textarea
                          value={sections[key]}
                          onChange={(e) => updateSection(key, e.target.value)}
                          placeholder={config.placeholder}
                          className="min-h-[150px] border-[#E2E8F0] focus:border-[#1A365D]"
                          data-testid={`section-${key}`}
                        />
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>

                {/* Submit Section */}
                <div className="mt-6 pt-4 border-t border-[#E2E8F0]">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <span className="text-sm font-medium text-[#1A365D]">Total : </span>
                      <span className={`text-sm font-bold ${getTotalWords() >= 300 ? 'text-green-600' : 'text-orange-500'}`}>
                        {getTotalWords()} mots
                      </span>
                      <span className="text-xs text-[#64748B] ml-2">(minimum 300)</span>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={submitAnswer}
                    disabled={submitting || getTotalWords() < 300}
                    className="w-full bg-[#D4AF37] hover:bg-[#B3932E] text-[#0F172A]"
                    data-testid="submit-button"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Evaluation en cours...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Soumettre mon commentaire
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default CommentaireModule;
